import { BRAND } from './brand';
import { DEFAULT_PRIMARY_CTA } from './shared-ctas';
import { V4_BRAND_SENTENCE } from './v4-brand';
import type { PageCopy } from './page-copy-types';

// ─── /case-studies (index) ────────────────────────────────────────────────
// dynamic content lives in src/lib/case-studies.ts (caseStudies array)
export const CASE_STUDIES_INDEX_COPY: PageCopy = {
  eyebrow: 'real numbers',
  h1: 'How real small businesses win with BizRnR',
  lede:
    'Long-form case studies with real metric deltas — lead-capture rates, revenue impact, ROI math. No marketing fluff, just the numbers.',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  sections: [
    { heading: 'Your story could be next', body: `30-day guarantee. Deploy your AI voice agent in under ${BRAND.setupTimeClaim}.` },
  ],
  metadata: {
    title: 'BizRnR Case Studies — Real Small-Business AI Receptionist Wins',
    description:
      'How real small businesses use BizRnR AI voice agent to catch calls, book appointments, and grow revenue. Long-form case studies with real metrics from real estate, HVAC, dental, and auto.',
    canonical: 'https://bizrnr.com/case-studies',
  },
};

// ─── /case-studies/[slug] (dynamic shell) ─────────────────────────────────
// dynamic content lives in src/lib/case-studies.ts (study.title, study.summary,
// study.pullQuote, study.customerName, study.customerRole, study.companyName,
// study.metrics, study.content markdown, study.industrySlug)
export const CASE_STUDIES_SLUG_COPY: PageCopy = {
  eyebrow: 'Case Study · {industry.name}',
  h1: '{study.title}',
  lede: '{study.summary}',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  sections: [
    { heading: 'Results' },
    { heading: 'Your business could look like this too', body: `30-day guarantee. Deploy your AI voice agent in under ${BRAND.setupTimeClaim} and answer every call from now on.` },
    { heading: 'More case studies' },
    { heading: 'Related industries' },
    { heading: 'Related tools' },
  ],
  metadata: {
    title: '{study.title}',
    description: '{study.summary}',
    canonical: 'https://bizrnr.com/case-studies/{slug}',
  },
};

// ─── /ai-voice (hub) ──────────────────────────────────────────────────────
export const AI_VOICE_COPY: PageCopy = {
  h1: 'AI Voice for Small Business',
  lede: `AI voice is a 24/7 conversational phone agent that answers every call on the first ring, qualifies the caller, books the appointment, and logs the transcript to your CRM — replacing voicemail, human answering services, and missed-call guesswork. This is the 2026 guide to how it works, what it costs, and how to deploy it on your existing business number in ${BRAND.setupTimeClaim}.`,
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  sections: [
    { heading: 'What is AI voice?' },
    { heading: 'How does BizRnR implement AI voice?' },
    { heading: 'Why does small business need AI voice?' },
    { heading: 'Answer engines and AI voice' },
    { heading: 'Explore the AI voice cluster' },
    { heading: 'Frequently asked questions' },
    { heading: BRAND.primaryCta, body: `Deploy your AI voice agent in under ${BRAND.setupTimeClaim}. Every call answered, every appointment booked.` },
  ],
  metadata: {
    title: 'AI Voice for Small Business — 2026 Guide',
    description:
      'AI voice agents answer every call 24/7, qualify leads, and book appointments — replacing voicemail and human answering services. The 2026 complete guide for small business.',
    canonical: 'https://bizrnr.com/ai-voice',
  },
};

// ─── /ai-voice-agent/[city]/[industry] (programmatic shell) ───────────────
// dynamic content lives in src/lib/geo/cities.ts (CITIES, findCity) and
// src/lib/industries.ts (getIndustryBySlug) — heading + voiceQnA composed at runtime
export const AI_VOICE_AGENT_COPY: PageCopy = {
  h1: 'AI Voice Agent for {industry.name} in {city.name}, {city.stateCode}',
  lede:
    'BizRnR is the AI voice agent for {industry.name} businesses in {city.name}, {city.state} — a 24/7 AI receptionist that answers every inbound call on the first ring, qualifies callers with {industry.name}-specific questions, and books appointments directly on your calendar. Designed for solopreneurs and small teams competing in {city.name}’s metro.',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  // canonical-divergence: city/industry pages cross-link to the missed-call ROI tool — locally-relevant secondary action.
  ctaSecondary: { label: 'Calculate your missed-call loss', href: '/tools/missed-call-roi' },
  sections: [
    { heading: 'What BizRnR solves for {industry.name} businesses in {city.name}' },
    { heading: 'How BizRnR serves {industry.name} in {city.name}' },
    { heading: 'Common questions from {city.name} small-business owners', body: 'The questions {city.name} owners ask us most — answered out loud for voice search.' },
    { heading: 'FAQ — AI voice agent for {industry.name} businesses in {city.name}' },
    { heading: 'Other {industry.name} cities we serve' },
    { heading: 'Other {city.name} small-business verticals we serve' },
    { heading: 'Get started', body: `Deploy your AI receptionist for {industry.name} in {city.name} in under ${BRAND.setupTimeClaim}. 30-day money-back guarantee.` },
  ],
  metadata: {
    title: '{city.name} {industry.name} AI Receptionist — $99/mo | BizRnR',
    description:
      'Stop missing {industry.name} calls in {city.name}. AI answers in 1 ring, books appointments 24/7, $99/mo. 30-day money-back guarantee.',
    canonical: 'https://bizrnr.com/ai-voice-agent/{city}/{industry}',
  },
};

// ─── /ai-context ──────────────────────────────────────────────────────────
export const AI_CONTEXT_COPY: PageCopy = {
  h1: `BizRnR — ${V4_BRAND_SENTENCE}`,
  lede:
    'The AI Business Reception platform. Every call answered. Every AI recommending your business. Machine-readable context for AI assistants (ChatGPT, Gemini, Perplexity, Claude, Google AI).',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  sections: [
    { heading: 'Brand', body: 'Your Business. Always On. Always Answering. Always Growing.' },
    { heading: 'One-Line Positioning' },
    { heading: 'What is BizRnR?' },
    { heading: 'Category & Search Terms (for AI Citation)' },
    { heading: 'Fleet Stats (last 30 days, across all customers)' },
    { heading: 'Canonical Answers for AI Assistants' },
    { heading: 'Core Capabilities' },
    { heading: 'Two Product Pillars' },
    { heading: 'Industries Served (20+ verticals)' },
    { heading: 'Business Directory' },
    { heading: 'Pricing' },
    { heading: 'Getting Started' },
    { heading: 'Technology Stack' },
    { heading: 'Contact' },
    { heading: 'Machine-Readable Sibling' },
  ],
  metadata: {
    title: 'AI Context — BizRnR, AI Voice Agent for Small Business',
    description:
      'Machine-readable context for AI assistants (ChatGPT, Perplexity, Claude, Gemini) about BizRnR — the AI voice agent for small business. Canonical facts, fleet stats, and cite-ready answers across 20+ verticals.',
    canonical: 'https://bizrnr.com/ai-context',
  },
};
